/* eslint-disable complexity */

/* eslint-disable max-lines-per-function */

/* eslint-disable promise/always-return */

/* eslint-disable promise/catch-or-return */
import React, { useContext } from 'react';

import debounce from 'lodash/debounce';

import {
  IssueService,
  StorageOfUniqueServices,
  TestExecutionStepLinkedIssueService,
} from '@qality/common-service';
import MapperService from '@qality/common-service/src/MapperService';
import { TestExecutionPageContext } from '@qality/common-state';

import { ReportBugsContext } from './ReportBugsContext';
import DescriptionUtils from './ReportBugsProvider.descriptionUtils';

export const ReportBugsProvider = ({
  children,
  linkedIssues = [],
  projectId,
  testExecutionStepId,
  onAddBug,
}: any) => {
  const getBrowseIssueUrl =
    StorageOfUniqueServices.get('URLService').getIssueURL;
  const [linkedIssuesList, setLinkedIssuesList] = React.useState(linkedIssues);
  const [openedOnce, setOpenedOnce] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [isLoadingSearchOptions, setIsLoadingSearchOptions] =
    React.useState(true);
  const [searchValue, setSearchValue] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const [isCreateOpen, setIsCreateOpen] = React.useState(false);
  const [isReportOpen, setIsReportOpen] = React.useState(false);
  const { testExecution } = useContext(TestExecutionPageContext);

  React.useEffect(() => {
    if (openedOnce) {
      loadLinkedIssuesList();
    }
  }, [openedOnce]);

  const init = () => {
    setOpenedOnce(true);
  };

  const createIssue = async () => {
    const description = await DescriptionUtils.getDescription(
      testExecution,
      testExecutionStepId
    );

    const createdIssues = await StorageOfUniqueServices.get(
      'FlagCreator'
    ).showCreateIssueDialogWithDescription('', '', projectId, description);

    if (createdIssues?.length > 0) {
      const [firstCreatedIssue] = createdIssues;

      TestExecutionStepLinkedIssueService.linkIssue(
        testExecutionStepId,
        firstCreatedIssue.id
      );
      setLinkedIssuesList((currentIssuesList) => {
        return [
          ...currentIssuesList,
          {
            linkedIssueId: firstCreatedIssue.id,
            ...MapperService.formatJIRAIssueToIssue(
              firstCreatedIssue,
              getBrowseIssueUrl
            ),
          },
        ];
      });
      setOpenedOnce(false);
      onAddBug();
    }
  };

  const addIssue = async (issueId: any) => {
    TestExecutionStepLinkedIssueService.linkIssue(testExecutionStepId, issueId);
    setLinkedIssuesList((currentIssuesList) => {
      return [...currentIssuesList, { linkedIssueId: issueId }];
    });
    setOpenedOnce(false);
    onAddBug();
  };

  const removeByIssueId = async (issueIdToRemove) => {
    TestExecutionStepLinkedIssueService.unlinkIssue(
      testExecutionStepId,
      issueIdToRemove
    );
    setLinkedIssuesList((currentLinkedIssuesList) =>
      currentLinkedIssuesList.filter(
        (linkedIssue) => linkedIssue.issueId !== issueIdToRemove
      )
    );
    onAddBug();
  };

  const debounceSearchRequest = React.useCallback(
    debounce(async (query, alreadyLinkedIssues) => {
      setIsLoadingSearchOptions(true);

      const response = await IssueService.searchIssuesByQuery(
        query,
        null,
        alreadyLinkedIssues,
        null,
        true // usePicker since an average user should have issues in his history to show here
      );

      const mappedIssues =
        response.issues.map((issue) =>
          MapperService.formatJIRAIssueToIssue(issue, getBrowseIssueUrl)
        ) || [];

      setSearchResults(mappedIssues);
      setIsLoadingSearchOptions(false);
    }, 350),
    [linkedIssuesList]
  );

  const searchIssueByQuery = async (query: string) => {
    setIsLoadingSearchOptions(true);

    const alreadyLinkedIssues = linkedIssuesList.map(
      ({ linkedIssueId }) => linkedIssueId
    );
    debounceSearchRequest(query, alreadyLinkedIssues);
  };

  const fetchIssues = async () => {
    const issueIds = linkedIssuesList.map(({ linkedIssueId }) => linkedIssueId);
    const issues = await StorageOfUniqueServices.get(
      'JQLSearchService'
    ).bulkFetchIssuesByIds({
      ids: issueIds,
      fields: ['summary', 'issuetype', 'created', 'status'],
      maxResults: 50,
    });

    const mappedIssuesFromService = issues.map((issue) =>
      MapperService.formatJIRAIssueToIssue(issue, getBrowseIssueUrl)
    );

    return linkedIssuesList.map((linkedIssue) => {
      const mappedIssue =
        mappedIssuesFromService.find(
          ({ issueId }) => `${issueId}` === `${linkedIssue.linkedIssueId}`
        ) || null;

      return {
        ...linkedIssue,
        ...mappedIssue,
      };
    });
  };

  const loadLinkedIssuesList = async () => {
    setIsLoading(true);
    const issuesList = await fetchIssues();
    if (issuesList) {
      setLinkedIssuesList(issuesList);
    }
    setIsLoading(false);
  };

  const handleSearchValueChange = (query: string) => {
    setSearchValue(query);
    searchIssueByQuery(query);
  };

  const handleCreateOpen = (value: boolean) => {
    setSearchValue('');
    searchIssueByQuery('');
    setIsCreateOpen(value);
  };

  const filteredSearchResults = searchResults.filter(
    (searchResultIssue) =>
      !linkedIssuesList.find((linkedIssue) => {
        return linkedIssue.linkedIssueId === searchResultIssue.issueId;
      })
  );

  // query once again when all list issues form list were added
  if (
    !isLoadingSearchOptions &&
    searchResults.length > 0 &&
    filteredSearchResults.length < 1
  ) {
    searchIssueByQuery(searchValue);
  }

  return (
    <ReportBugsContext.Provider
      value={{
        issues: linkedIssuesList.filter(({ issueId }) => issueId),
        init,
        addIssue,
        createIssue,
        projectId,
        isLoading,
        searchValue,
        setSearchValue: handleSearchValueChange,
        isCreateOpen,
        setIsCreateOpen: handleCreateOpen,
        isReportOpen,
        setIsReportOpen,
        isLoadingSearchOptions,
        searchResults: filteredSearchResults,
        notFetchedCount: isLoading
          ? 0
          : linkedIssuesList.filter(({ issueId }) => !issueId).length,
        removeByIssueId,
        linkedIssuesCount: linkedIssuesList.length,
      }}
    >
      {children}
    </ReportBugsContext.Provider>
  );
};
